/**
 * useBuildFilterAttributes — parallel to useShopFilterAttributes for /build.
 */

import { useEffect, useMemo, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export interface BuildAttributeValueDto {
  readonly attribute_id: string
  readonly attribute_label: string
  readonly value_id: string
  readonly value_label: string
}

export interface BuildAttributeGroup {
  readonly attribute_id: string
  readonly attribute_label: string
  readonly values: ReadonlyArray<{ readonly id: string; readonly label: string }>
}

interface Body {
  readonly success: true
  readonly data: { readonly items: BuildAttributeValueDto[] }
}

export interface UseBuildFilterAttributesReturn {
  readonly groups: ReadonlyArray<BuildAttributeGroup>
  readonly isLoading: boolean
  readonly error: string | null
}

export function useBuildFilterAttributes(
  categoryIds: ReadonlyArray<string>
): UseBuildFilterAttributesReturn {
  const [items, setItems] = useState<BuildAttributeValueDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const key = useMemo(() => [...categoryIds].sort().join(','), [categoryIds])

  useEffect(() => {
    if (key.length === 0) {
      setItems([])
      setError(null)
      setIsLoading(false)
      return
    }
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(
            `${APIBASE}${APIROUTES.BUILDFILTERATTRIBUTES}?categoryIds=${encodeURIComponent(key)}`,
            { signal: ac.signal }
          )
        } catch (err) {
          if (cancelled) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled) return
        if (res.status >= 500 || !res.ok) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        const body = (await res.json()) as Body
        if (!cancelled) setItems(body.data.items)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
          setItems([])
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [key])

  const groups = useMemo<ReadonlyArray<BuildAttributeGroup>>(() => {
    const byId = new Map<string, BuildAttributeGroup>()
    for (const row of items) {
      let g = byId.get(row.attribute_id)
      if (g === undefined) {
        g = { attribute_id: row.attribute_id, attribute_label: row.attribute_label, values: [] }
        byId.set(row.attribute_id, g)
      }
      ;(g.values as Array<{ id: string; label: string }>).push({
        id: row.value_id,
        label: row.value_label
      })
    }
    return [...byId.values()]
  }, [items])

  return { groups, isLoading, error }
}
